import React, {useState, useEffect, useCallback} from 'react';
import {Typography, Video} from '../../elements';
import root from 'window-or-global';
import {Timeline} from './Timeline';
import {AccentColorOptions} from 'epic-ue-theme';
import {
    HomepageHeaderContainer,
    HomepageWrapper,
    HomepageHeaderWrapper
} from './UniverseCarousel.styles';
import {IndustryProps, BranchProps} from './HomepageHeader.types';
import Grid from '@material-ui/core/Grid';
import {AnimateOnViewWrap} from '../../wraps/animation';
import {InView} from 'react-intersection-observer';
import classNames from 'classnames';
import {MouseHijack} from '../../wraps';
import {LocalVideoOptions} from '../../../types';
import {useBrowserSatisfies} from 'epic-ue-shared';

export interface UniverseCarouselProps {
    industries: IndustryProps[];
    id: string;
    lockScroll: (boolean) => void;
    introDoneCallback: (boolean) => void;
}

export const UniverseCarousel = ({
    industries,
    lockScroll,
    introDoneCallback,
    id
}: UniverseCarouselProps): JSX.Element => {
    const [toggleReel, setToggleReel] = useState(false);
    const [scaleUpVideo, setScaleUpVideo] = useState(false);
    const [autoPlay, setAutoPlay] = useState(true);
    const [btnClicked, setBtnClicked] = useState('');
    const [nextIndustry, setNextIndustry] = useState('');
    const [activeIndustry, setActiveIndustry] = useState('');
    const [hasLoaded, setHasLoaded] = useState(false);
    const [activeUniverse, setActiveUniverse] = useState('');
    const [industryReelUrl, setIndustryReelUrl] = useState<LocalVideoOptions>({
        content: ['']
    });
    const [inView, setInView] = useState(false);
    const [showIntro, setShowIntro] = useState(true);
    const disableForSlowMachines = useBrowserSatisfies({
        safari: '<14',
        'internet explorer': '>8',
        chrome: '<70',
        firefox: '<70'
    });

    const inViewChange = useCallback(
        (inView) => {
            if (inView) {
                setInView(true);
                if (!showIntro) {
                    setAutoPlay(true);
                }
                if (lockScroll && typeof lockScroll === 'function') {
                    if (showIntro && activeIndustry === '') {
                        //lockScroll(true);
                    }
                }
            } else if (!inView) {
                setInView(false);
            }
        },
        [inView, setInView, setAutoPlay, showIntro, activeIndustry]
    );

    // All the industries shown on the page in order as ordered in the CMS.
    // This order is used to deduce the index for the timeline autoplay.
    const IndustryArrayList: string[] = [];

    let gamesVideo = '';
    let filmVideo = '';
    let aecVideo = '';
    let autoVideo = '';
    let broadcastVideo = '';
    let trainingVideo = '';

    let moreVideo = '';
    let universeElements: (JSX.Element | undefined)[] = [<div key="universeElements" />];
    let activeUniverseBranches: BranchProps[] = [];
    let activeFrameImg = '';
    let activeTitle = '';
    let activeDesc = '';
    let activeUrl = '';
    let activeAccentColor: AccentColorOptions = 'sky';

    const findOrigin = (universe) => {
        return universe.origin === activeIndustry;
    };

    if (industries) {
        const activeUniverseObj = industries.find(findOrigin);

        // Assign the reels for each industry to a variable we can use later on.
        // Games is passed in separatly as it is the first to play and set as default in the state.
        industries.map((industry, i) => {
            if (industry) {
                const industryOrigin = industry.origin || '';
                IndustryArrayList.push(industryOrigin);

                switch (industryOrigin) {
                    case 'games':
                        gamesVideo = industry.reel || '';
                        break;
                    case 'film':
                        filmVideo = industry.reel || '';
                        break;
                    case 'aec':
                        aecVideo = industry.reel || '';
                        break;
                    case 'automotive':
                        autoVideo = industry.reel || '';
                        break;
                    case 'broadcast':
                        broadcastVideo = industry.reel || '';
                        break;
                    case 'training':
                        trainingVideo = industry.reel || '';
                        break;
                    case 'more':
                        moreVideo = industry.reel || '';
                        break;
                    default:
                        break;
                }
            }
        });

        // Remove the current industry from the array so we are left with 6 industries.
        // we'll use this array to build the universe videos.
        for (let i = IndustryArrayList.length - 1; i >= 0; i--) {
            if (IndustryArrayList[i] === activeIndustry) {
                IndustryArrayList.splice(i, 1);
                break;
            }
        }

        //Get all the data from the current active industry
        if (activeUniverseObj) {
            activeUniverseBranches = activeUniverseObj.branches || [];
            activeFrameImg = activeUniverseObj.frameImg || '';
            activeTitle = activeUniverseObj.tagLine || '';
            activeDesc = activeUniverseObj.desc || '';
            activeUrl = activeUniverseObj.url || '';
            activeAccentColor = activeUniverseObj.accentColor as AccentColorOptions;
        }
    }

    const universeEnded = (e) => {
        //wait for the universe to end
        setBtnClicked('');

        setAutoPlay(true);

        //Release scroll Lock
        if (lockScroll && typeof lockScroll === 'function') {
            lockScroll(false);
        }

        // Reset all the relevant states.
        setToggleReel(false);
        setActiveUniverse('');
        setActiveIndustry(nextIndustry);
    };

    const introDone = (e) => {
        setIndustryReelUrl({
            content: [gamesVideo]
        });

        setActiveIndustry('games');
        setShowIntro(false);
        setAutoPlay(true);

        //Release scroll Lock
        if (lockScroll && typeof lockScroll === 'function') {
            lockScroll(false);
        }

        // Let parent know the intro has been played
        if (introDoneCallback && typeof introDoneCallback === 'function') {
            introDoneCallback(true);
        }
    };

    const scaleVideo = (direction) => {
        if (!showIntro && activeIndustry && !toggleReel && !disableForSlowMachines) {
            if (direction === 'up') {
                setScaleUpVideo(true);
                setAutoPlay(false);
            } else {
                setScaleUpVideo(false);
                setAutoPlay(true);
            }
        }
    };

    const swapVideo = (e) => {
        if (e) {
            setAutoPlay(false);

            // get current and next industry names
            let nextIndustryId = '';
            if (e.currentTarget === undefined) {
                // This is coming from the autoplay timeline.
                nextIndustryId = e;
            } else {
                nextIndustryId = e.currentTarget.id;
            }

            //Name of the tranisition
            const universeTransition = `${activeIndustry}to${nextIndustryId}`;

            //disable button
            setBtnClicked(nextIndustryId);

            //set active to new industry
            setNextIndustry(nextIndustryId);

            //Set state of active universe for later use
            setActiveUniverse(universeTransition);

            //find and show universe transition
            setToggleReel(true);

            //find the right universe video and play it.
            const universeVideo = root.document.getElementById('universe');
            if (universeVideo) {
                const universeVideoElements = universeVideo.querySelectorAll('[data-universe]');
                for (const i in universeVideoElements) {
                    if (Object.hasOwnProperty.call(universeVideoElements, i)) {
                        const transitionAttr = universeVideoElements[i].getAttribute(
                            'data-universe'
                        );

                        if (transitionAttr === universeTransition) {
                            universeVideoElements[i].load();
                            universeVideoElements[i].play();
                            if (lockScroll && typeof lockScroll === 'function') {
                                // EGS-37341 Disabling scrollLocking for now due to slow connection issues
                                //lockScroll(true);
                            }
                            setAutoPlay(false);
                        }
                    }
                }
            }

            //switch the source of the main reel while the universe is playing.
            switch (nextIndustryId) {
                case 'games':
                    setIndustryReelUrl({
                        content: [gamesVideo]
                    });
                    break;
                case 'film':
                    setIndustryReelUrl({
                        content: [filmVideo]
                    });
                    break;
                case 'aec':
                    setIndustryReelUrl({
                        content: [aecVideo]
                    });
                    break;
                case 'automotive':
                    setIndustryReelUrl({
                        content: [autoVideo]
                    });
                    break;
                case 'broadcast':
                    setIndustryReelUrl({
                        content: [broadcastVideo]
                    });
                    break;
                case 'training':
                    setIndustryReelUrl({
                        content: [trainingVideo]
                    });
                    break;
                case 'more':
                    setIndustryReelUrl({
                        content: [moreVideo]
                    });
                    break;
                default:
                    setIndustryReelUrl({
                        content: [gamesVideo]
                    });
                    break;
            }
        }
    };

    useEffect(() => {
        if (!hasLoaded) {
            setAutoPlay(false);
        }
        if (inView) {
            setInView(true);
        } else if (!inView) {
            setInView(false);
        }

        setHasLoaded(true);
    }, [setHasLoaded, setAutoPlay, lockScroll, introDone, setInView, hasLoaded, inView]);

    // Create the elemenents of each industry transition.
    universeElements = activeUniverseBranches.map((branch, i) => {
        const branchName = branch.name || '';
        if (IndustryArrayList.includes(branchName)) {
            return (
                <video
                    preload="auto"
                    data-universe={`${activeIndustry}to${branchName}`}
                    muted
                    key={`video-universe-${branchName}-${i}`}
                    onEnded={universeEnded}
                    className={`universe-video ${
                        activeUniverse === `${activeIndustry}to${branchName}` ? 'active' : ''
                    }`}>
                    <source src={branch.url} type="video/mp4" />
                </video>
            );
        }
    });

    return (
        <HomepageHeaderWrapper>
            <HomepageHeaderContainer
                id={id}
                className={classNames({
                    activeIndustry,
                    inView,
                    toggling: toggleReel
                })}>
                <div className={`${showIntro ? 'timeline-hide' : 'timeline-show'}`}>
                    <MouseHijack href={activeUrl} accentColor={activeAccentColor}>
                        <div
                            className="scalebox"
                            onMouseEnter={() => scaleVideo('up')}
                            onMouseLeave={() => scaleVideo('down')}
                        />
                    </MouseHijack>
                    <AnimateOnViewWrap delay={0.25} duration={0.5} translateY={30}>
                        <div className={`timeline-wrapper ${toggleReel ? 'toggle' : ''}`}>
                            <Timeline
                                activeIndustry={activeIndustry}
                                accentColor={activeAccentColor}
                                autoPlay={autoPlay}
                                inUniverse={toggleReel}
                                universeData={industries}
                                onClick={swapVideo}
                                btnClicked={btnClicked}
                            />
                        </div>
                    </AnimateOnViewWrap>
                </div>

                <HomepageWrapper
                    className={`${toggleReel ? 'toggle' : ''} ${hasLoaded ? 'loaded' : ''}`}>
                    <div className="content">
                        <Grid container alignItems="flex-start">
                            <Grid item xs={1} />
                            <Grid item xs={4}>
                                <Typography variant="h2" big={true}>
                                    {activeTitle}
                                </Typography>
                            </Grid>
                            <Grid item xs={2} />
                            <Grid item xs={4}>
                                <Typography variant="p">{activeDesc}</Typography>
                            </Grid>
                        </Grid>
                    </div>
                    <div
                        id="universe"
                        className={`playing-${activeIndustry} ${toggleReel ? 'show' : ''}`}>
                        {universeElements}
                    </div>
                    <InView as="div" onChange={inViewChange}>
                        <div
                            id="mainVideo"
                            style={{
                                backgroundImage: `url(${activeFrameImg})`
                            }}
                            className={`playing-${activeIndustry} 
                                    ${inView ? 'inView' : 'notInView'}
                                    ${scaleUpVideo ? 'scale-up' : ''}
                                    ${toggleReel || showIntro ? 'hide' : 'playing'}`}>
                            <Video
                                key={activeIndustry}
                                mediaOptions={industryReelUrl}
                                gradient={false}
                            />
                        </div>
                    </InView>
                    <div id="intro" className={`${showIntro ? 'show' : 'hide'}`}>
                        <video
                            preload="auto"
                            playsInline
                            muted
                            autoPlay={activeIndustry === ''}
                            onEnded={introDone}>
                            <source
                                src={
                                    'https://cdn2.unrealengine.com/intro-fast-compressed-2d5502a8c936.mp4'
                                }
                                type="video/mp4"
                            />
                        </video>
                    </div>
                </HomepageWrapper>
            </HomepageHeaderContainer>
        </HomepageHeaderWrapper>
    );
};
